import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Pagination } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { getTickerNotes, addTickerNote, editTickerNote, deleteTickerNote, togglePinNote } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

const TickerNotesPanel = ({ selectedItem, getTickerIdForItem, onNoteChange }) => {
  const [notesDraft, setNotesDraft] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNoteIdx, setEditingNoteIdx] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [notesPage, setNotesPage] = useState(1);
  const [showDeleteNoteConfirm, setShowDeleteNoteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const textareaRef = React.useRef(null);

  const notesPerPage = 5;

  // Auto-resize textarea when editing note text changes
  React.useEffect(() => {
    if (textareaRef.current && editingNoteIdx !== null) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 400)}px`;
    }
  }, [editingNoteText, editingNoteIdx]);

  // Load notes when selectedItem changes
  useEffect(() => {
    const loadNotes = async () => {
      if (selectedItem) {
        setLoading(true);
        try {
          const tickerId = getTickerIdForItem(selectedItem);
          if (tickerId) {
            const res = await getTickerNotes(tickerId);
            setNotesDraft(res.data);
          } else {
            setNotesDraft([]);
          }
        } catch (error) {
          console.error('Error loading notes:', error);
          setNotesDraft([]);
        } finally {
          setLoading(false);
        }
      } else {
        setNotesDraft([]);
        setLoading(false);
      }
    };

    loadNotes();
    setNewNote('');
    setEditingNoteIdx(null);
    setEditingNoteText('');
    setNotesPage(1); // Reset to first page when switching tickers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  const handleAddNote = async () => {
    if (!selectedItem || !newNote.trim()) return;

    try {
      const tickerId = getTickerIdForItem(selectedItem);
      if (!tickerId) {
        console.error('Could not find ticker ID for item');
        return;
      }

      await addTickerNote(tickerId, newNote);
      setNewNote('');
      setShowAddNoteModal(false);

      // Refresh notes
      const res = await getTickerNotes(tickerId);
      setNotesDraft(res.data);

      // Notify parent component of note change
      if (onNoteChange) {
        onNoteChange(tickerId);
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleEditNote = (idx) => {
    setEditingNoteIdx(idx);
    setEditingNoteText(notesDraft[idx].note);
  };

  const handleSaveEditNote = async () => {
    if (editingNoteIdx === null || !selectedItem) return;

    try {
      const tickerId = getTickerIdForItem(selectedItem);
      if (!tickerId) {
        console.error('Could not find ticker ID for item');
        return;
      }

      const noteToEdit = notesDraft[editingNoteIdx];
      if (!noteToEdit || !noteToEdit.id) {
        console.error('Could not find note to edit');
        return;
      }

      await editTickerNote(noteToEdit.id, editingNoteText);
      setEditingNoteIdx(null);
      setEditingNoteText('');

      // Refresh notes
      const res = await getTickerNotes(tickerId);
      setNotesDraft(res.data);

      // Notify parent component of note change
      if (onNoteChange) {
        onNoteChange(tickerId);
      }
    } catch (error) {
      console.error('Error editing note:', error);
    }
  };

  const handleDeleteNoteClick = (idx) => {
    setNoteToDelete(idx);
    setShowDeleteNoteConfirm(true);
  };

  const handleDeleteNoteConfirm = async () => {
    if (!selectedItem || noteToDelete === null) return;

    try {
      const tickerId = getTickerIdForItem(selectedItem);
      if (!tickerId) {
        console.error('Could not find ticker ID for item');
        return;
      }

      const noteToDeleteObj = notesDraft[noteToDelete];
      if (!noteToDeleteObj || !noteToDeleteObj.id) {
        console.error('Could not find note to delete');
        return;
      }

      await deleteTickerNote(noteToDeleteObj.id);
      setShowDeleteNoteConfirm(false);
      setNoteToDelete(null);

      // Refresh notes
      const res = await getTickerNotes(tickerId);
      setNotesDraft(res.data);

      // Notify parent component of note change
      if (onNoteChange) {
        onNoteChange(tickerId);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleDeleteNoteCancel = () => {
    setShowDeleteNoteConfirm(false);
    setNoteToDelete(null);
  };

  const handleTogglePin = async (idx) => {
    if (!selectedItem) return;

    try {
      const tickerId = getTickerIdForItem(selectedItem);
      if (!tickerId) {
        console.error('Could not find ticker ID for item');
        return;
      }

      const noteToToggle = notesDraft[idx];
      if (!noteToToggle || !noteToToggle.id) {
        console.error('Could not find note to toggle pin');
        return;
      }

      // Optimistically update UI
      const updatedNotes = [...notesDraft];
      updatedNotes[idx] = { ...noteToToggle, pinned: !noteToToggle.pinned };

      // Sort: pinned first, then by updated_at
      updatedNotes.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });

      setNotesDraft(updatedNotes);

      // Make API call
      await togglePinNote(noteToToggle.id);

      // Refresh notes to ensure sync
      const res = await getTickerNotes(tickerId);
      setNotesDraft(res.data);

      // Notify parent component of note change
      if (onNoteChange) {
        onNoteChange(tickerId);
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
      // Refresh notes on error to revert optimistic update
      const tickerId = getTickerIdForItem(selectedItem);
      if (tickerId) {
        const res = await getTickerNotes(tickerId);
        setNotesDraft(res.data);
      }
    }
  };

  if (!selectedItem) {
    return (
      <div className="watchlist-notes-panel watchlist-notes-empty">
        Select a ticker to view notes.
      </div>
    );
  }

  const ticker = selectedItem.ticker?.toUpperCase() || '';
  const companyName = selectedItem.company_name || '';
  const totalPages = Math.ceil(notesDraft.length / notesPerPage);
  const startIndex = (notesPage - 1) * notesPerPage;
  const endIndex = startIndex + notesPerPage;
  const currentNotes = notesDraft.slice(startIndex, endIndex);

  return (
    <>
      <div className="watchlist-notes-panel">
        {/* Ticker symbol and company name as title */}
        <div className="watchlist-notes-header selected" style={{ fontSize: '1.1em', marginBottom: '1rem', letterSpacing: '0.02em', lineHeight: 1.1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '1.1em' }}>{ticker}</div>
            {companyName && (
              <div style={{ fontSize: '0.85em', opacity: 0.8, marginTop: '0.2rem' }}>
                {companyName}
              </div>
            )}
          </div>
          <FaPlus
            onClick={() => setShowAddNoteModal(true)}
            style={{ cursor: 'pointer', color: '#28a745', fontSize: '1em', marginTop: '0.2rem' }}
            title="Add new note"
          />
        </div>
        <div>
          {loading ? (
            <div className="watchlist-notes-empty" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span>Loading notes...</span>
            </div>
          ) : notesDraft.length === 0 ? (
            <div className="watchlist-notes-empty">No notes yet.</div>
          ) : null}
          {!loading && currentNotes.map((note, idx) => {
            const originalIdx = startIndex + idx;
            return (
              <div key={note.id || originalIdx} className="watchlist-note-item" style={{
                marginBottom: 8,
                background: note.pinned ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)' : 'transparent',
                borderLeft: note.pinned ? '3px solid #ffc107' : 'none',
                paddingLeft: note.pinned ? '8px' : '0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.9em', color: '#888' }}>{formatDateTime(note.updated_at || note.created_at || note.date)}</div>
                  {editingNoteIdx !== originalIdx && (
                    <div style={{ display: 'flex', gap: '0.5rem', margin: 0 }}>
                      <i
                        className={note.pinned ? "bi bi-pin-fill" : "bi bi-pin"}
                        onClick={() => handleTogglePin(originalIdx)}
                        style={{
                          cursor: 'pointer',
                          color: note.pinned ? '#0d6efd' : '#6c757d',
                          fontSize: '0.9em'
                        }}
                        title={note.pinned ? "Unpin" : "Pin to top"}
                      />
                      <FaEdit
                        onClick={() => handleEditNote(originalIdx)}
                        style={{ cursor: 'pointer', color: '#007bff', fontSize: '0.9em' }}
                        title="Edit note"
                      />
                      <FaTrash
                        onClick={() => handleDeleteNoteClick(originalIdx)}
                        style={{ cursor: 'pointer', color: '#dc3545', fontSize: '0.9em' }}
                        title="Delete note"
                      />
                    </div>
                  )}
                </div>
                {editingNoteIdx === originalIdx ? (
                  <>
                    <textarea
                      ref={textareaRef}
                      className="watchlist-notes-textarea"
                      value={editingNoteText}
                      onChange={e => setEditingNoteText(e.target.value)}
                      style={{
                        minHeight: '80px',
                        maxHeight: '400px',
                        resize: 'vertical',
                        overflow: 'auto',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div className="watchlist-notes-actions">
                      <Button size="sm" variant="primary" onClick={handleSaveEditNote}>Save</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingNoteIdx(null)}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <div className="watchlist-notes-readonly">{note.note || note.text}</div>
                )}
              </div>
            );
          })}

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted small">
                {notesDraft.length > 0 ? (
                  <>
                    Showing {startIndex + 1} to {Math.min(endIndex, notesDraft.length)} of {notesDraft.length} notes
                  </>
                ) : (
                  'No notes yet'
                )}
              </div>

              <Pagination size="sm">
                <Pagination.First
                  onClick={() => setNotesPage(1)}
                  disabled={notesPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setNotesPage(Math.max(1, notesPage - 1))}
                  disabled={notesPage === 1}
                />

                {(() => {
                  const items = [];
                  const maxItems = 5;

                  // Add first page
                  items.push(
                    <Pagination.Item
                      key={1}
                      active={notesPage === 1}
                      onClick={() => setNotesPage(1)}
                    >
                      1
                    </Pagination.Item>
                  );

                  // Calculate start and end pages
                  let startPage = Math.max(2, notesPage - 1);
                  let endPage = Math.min(totalPages - 1, notesPage + 1);

                  // Adjust for edge cases
                  if (notesPage <= 2) {
                    endPage = Math.min(totalPages - 1, maxItems - 2);
                  } else if (notesPage >= totalPages - 1) {
                    startPage = Math.max(2, totalPages - maxItems + 2);
                  }

                  // Add ellipsis after first page if needed
                  if (startPage > 2) {
                    items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
                  }

                  // Add intermediate pages
                  for (let i = startPage; i <= endPage; i++) {
                    items.push(
                      <Pagination.Item
                        key={i}
                        active={notesPage === i}
                        onClick={() => setNotesPage(i)}
                      >
                        {i}
                      </Pagination.Item>
                    );
                  }

                  // Add ellipsis before last page if needed
                  if (endPage < totalPages - 1) {
                    items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
                  }

                  // Add last page if there are more than 1 page
                  if (totalPages > 1) {
                    items.push(
                      <Pagination.Item
                        key={totalPages}
                        active={notesPage === totalPages}
                        onClick={() => setNotesPage(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    );
                  }

                  return items;
                })()}

                <Pagination.Next
                  onClick={() => setNotesPage(Math.min(totalPages, notesPage + 1))}
                  disabled={notesPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setNotesPage(totalPages)}
                  disabled={notesPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {/* Delete Note Confirmation Modal */}
      <Modal show={showDeleteNoteConfirm} onHide={handleDeleteNoteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Note?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this note? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteNoteCancel}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteNoteConfirm}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Add Note Modal */}
      <Modal show={showAddNoteModal} onHide={() => { setShowAddNoteModal(false); setNewNote(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={4}
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder="Enter your note..."
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddNoteModal(false); setNewNote(''); }}>Cancel</Button>
          <Button variant="primary" onClick={handleAddNote} disabled={!newNote.trim()}>Save Note</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TickerNotesPanel;
